import { useEffect, useId } from "react";

/** iter364 — plus AUCUN `beforeunload` (fenêtre blanche du navigateur interdite). Registre des formulaires modifiés
 *  + dernier champ touché (écran · champ) + brouillon sessionStorage repris au retour après un rechargement. */
const dirty = new Set();
export const DRAFT_KEY = "ledesk_draft";
let lastField = null;
let lastRoot = null; // dialogue / formulaire où la dernière saisie a eu lieu (saisie « en cours » tant qu'il est affiché)

const labelOf = (el) => {
  const byAria = el.getAttribute?.("aria-label") || el.getAttribute?.("placeholder");
  const id = el.id && document.querySelector(`label[for="${el.id}"]`)?.textContent;
  const wrap = el.closest?.("label")?.textContent;
  const prev = el.closest?.("div")?.querySelector?.("label, [data-label], span")?.textContent;
  return (id || wrap || byAria || prev || el.name || el.getAttribute?.("data-testid") || "champ").trim().replace(/\s*\*$/, "").slice(0, 40);
};

if (typeof window !== "undefined" && !window.__deskUnsavedInstalled) {
  window.__deskUnsavedInstalled = true;
  document.addEventListener("input", (e) => { const t = e.target; if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable)) { lastField = labelOf(t); lastRoot = t.closest('[role="dialog"], form, [data-dirty-scope]') || null; } }, true);
  // Bouton ↻ du navigateur (non interceptable) : la saisie du dialogue ouvert est gardée en brouillon.
  window.addEventListener("pagehide", () => { if (hasUnsavedChanges()) saveDraft(); });
  // Réouverture d'un dialogue après rechargement : brouillon ré-appliqué + bandeau « Brouillon restauré · hh:mm ».
  new MutationObserver(() => {
    const d = readDraft();
    if (!d) return;
    const root = [...document.querySelectorAll('[role="dialog"]')].reverse().find((r) => !r.__deskDraftTried);
    if (!root) return;
    root.__deskDraftTried = true;
    setTimeout(() => {
      const n = applyDraft(root, d);
      if (!n) return; // pas le bon dialogue (ex. sélecteur) : le brouillon reste disponible
      dropDraft();
      lastRoot = root;
      const b = document.createElement("div");
      b.setAttribute("data-testid", "dialog-draft-restored");
      b.style.cssText = "position:absolute;top:8px;left:50%;transform:translateX(-50%);z-index:5;font:11px Tahoma,sans-serif;color:#E0A42E;border:1px dashed #6a5226;background:#1a160e;border-radius:8px;padding:4px 10px;display:flex;gap:10px;align-items:center";
      b.innerHTML = `Brouillon restauré · ${new Date(d.at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} <button type="button" data-testid="dialog-draft-dismiss" style="color:#9a9aa3;font-size:10px">OK</button>`;
      b.querySelector("button").onclick = () => b.remove();
      (root.querySelector("[data-state], div") || root).appendChild(b);
    }, 120);
  }).observe(document.body, { childList: true, subtree: true });
}

export function hasUnsavedChanges() { return dirty.size > 0 || !!(lastRoot && document.contains(lastRoot)); }
export function clearUnsavedChanges() { dirty.clear(); lastRoot = null; }
export function unsavedContext() {
  // Écran = fil d'Ariane de l'en-tête (ex. « Commercial › Contacts »), sinon le premier titre de la page.
  const crumbEl = document.querySelector('[data-testid="crm-title"]');
  const crumbs = crumbEl ? [...crumbEl.parentElement.querySelectorAll("a, span")].map((e) => e.textContent.trim()).filter((t, i, a) => t && a.indexOf(t) === i) : [];
  const h1 = document.querySelector("main h1, h1")?.textContent?.trim();
  const screen = (crumbs.length ? crumbs.join(" › ") : h1) || window.location.pathname;
  return { screen, field: lastField };
}

/** Brouillon : valeurs des champs (data-testid) du dialogue ouvert, par écran. */
export function saveDraft() {
  const root = (lastRoot && document.contains(lastRoot) ? lastRoot : null) || document.querySelector('[role="dialog"][data-state="open"]') || document.querySelector('[role="dialog"]');
  if (!root) return;
  const values = {};
  root.querySelectorAll("input[data-testid], textarea[data-testid], select[data-testid]").forEach((el) => {
    if (el.type === "file" || el.type === "password") return;
    values[el.getAttribute("data-testid")] = el.type === "checkbox" ? !!el.checked : el.value;
  });
  if (!Object.keys(values).length) return;
  sessionStorage.setItem(`${DRAFT_KEY}:${window.location.pathname}`, JSON.stringify({ at: new Date().toISOString(), dialog: root.getAttribute("data-testid") || root.getAttribute("aria-labelledby") || "", values }));
}
export function readDraft() { try { return JSON.parse(sessionStorage.getItem(`${DRAFT_KEY}:${window.location.pathname}`) || "null"); } catch { return null; } }
export function dropDraft() { sessionStorage.removeItem(`${DRAFT_KEY}:${window.location.pathname}`); }
/** Ré-applique un brouillon aux champs présents (événements natifs pour React). Retourne le nombre de champs restaurés. */
export function applyDraft(root, draft) {
  let n = 0;
  Object.entries(draft.values || {}).forEach(([tid, v]) => {
    const el = root.querySelector(`[data-testid="${tid}"]`);
    if (!el) return;
    if (el.type === "checkbox") { if (!!el.checked !== !!v) el.click(); n += 1; return; }
    const proto = el.tagName === "TEXTAREA" ? HTMLTextAreaElement.prototype : el.tagName === "SELECT" ? HTMLSelectElement.prototype : HTMLInputElement.prototype;
    Object.getOwnPropertyDescriptor(proto, "value")?.set?.call(el, v);
    el.dispatchEvent(new Event(el.tagName === "SELECT" ? "change" : "input", { bubbles: true }));
    n += 1;
  });
  return n;
}

/** Déclare l'état « modifié » d'un formulaire. */
export function useUnsavedChanges(isDirty) {
  const id = useId();
  useEffect(() => {
    if (isDirty) dirty.add(id); else dirty.delete(id);
    return () => { dirty.delete(id); };
  }, [isDirty, id]);
}
